import { useState } from "react";
import { useI18n } from "../context/I18nContext";
import { analyzeSchedule } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, X, AlertTriangle, TrendingDown, Wrench, AlertOctagon } from "lucide-react";

const AI_HEADER = "https://static.prod-images.emergentagent.com/jobs/de8b7e50-5120-4fa2-90f1-4b615beda34b/images/9568f814b00d1e0c246980029c26bb1f48d29442eb966f8980918a6d94a48929.png";

const TYPES = [
  { key: "general", labelKey: "type_general" },
  { key: "critical_path", labelKey: "type_critical" },
  { key: "bottlenecks", labelKey: "type_bottlenecks" },
  { key: "delays", labelKey: "type_delays" },
  { key: "optimization", labelKey: "type_optimization" },
  { key: "inconsistencies", labelKey: "type_inconsistencies" },
];

export default function AIAnalysisPanel({ syncId, open, onClose }) {
  const { t } = useI18n();
  const [type, setType] = useState("general");
  const [extra, setExtra] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onAnalyze = async () => {
    setLoading(true); setResult(null);
    try {
      const r = await analyzeSchedule({ sync_id: syncId, analysis_type: type, extra_context: extra || undefined });
      setResult(r.result);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setLoading(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="ai-panel">
      <div className="flex-1 bg-zinc-950/40" onClick={onClose} />
      <aside className="w-full max-w-xl bg-white border-l border-zinc-200 shadow-2xl flex flex-col animate-fade-in">
        {/* Header */}
        <div className="relative h-32 overflow-hidden border-b border-zinc-200">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${AI_HEADER}')` }} />
          <div className="absolute inset-0 bg-zinc-950/70" />
          <div className="relative h-full flex flex-col justify-end p-6 text-white">
            <div className="cs-label !text-white/60 flex items-center gap-1.5">
              <Sparkles size={12} className="text-cs-accent" /> ai-powered
            </div>
            <h2 className="font-heading text-2xl font-bold mt-1">{t("ai_analysis")}</h2>
            <p className="text-xs text-white/70 mt-1">{t("ai_subtitle")}</p>
          </div>
          <button onClick={onClose} className="absolute top-4 right-4 p-1.5 bg-white/10 hover:bg-white/20 rounded text-white" data-testid="ai-close-btn">
            <X size={16} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 scrollbar-thin">
          <label className="cs-label block mb-1.5">{t("analysis_type")}</label>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {TYPES.map((tt) => (
              <button key={tt.key} onClick={() => setType(tt.key)}
                className={`text-xs px-2 py-2 rounded-md border transition-colors ${type === tt.key ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"}`}
                data-testid={`ai-type-${tt.key}`}
              >
                {t(tt.labelKey)}
              </button>
            ))}
          </div>

          <label className="cs-label block mb-1.5">{t("extra_context")}</label>
          <textarea value={extra} onChange={(e) => setExtra(e.target.value)} rows={3}
            placeholder="Ex: pavimento térreo em atraso por chuvas…"
            className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cs-accent" data-testid="ai-extra-input" />

          <button onClick={onAnalyze} disabled={loading}
            className="mt-4 w-full bg-cs-accent text-white py-2.5 rounded-md text-sm font-semibold hover:bg-cs-accentHover disabled:opacity-60 inline-flex items-center justify-center gap-2"
            data-testid="ai-analyze-btn"
          >
            <Sparkles size={14} /> {loading ? t("generating") : t("analyze_schedule_btn")}
          </button>

          {result && (
            <div className="mt-6 space-y-5" data-testid="ai-result">
              {result.summary && (
                <section>
                  <div className="cs-label mb-1.5">{t("summary")}</div>
                  <p className="text-sm text-zinc-800 leading-relaxed text-balance">{result.summary}</p>
                </section>
              )}

              {result.key_metrics && (
                <section>
                  <div className="cs-label mb-1.5">{t("key_metrics")}</div>
                  <div className="grid grid-cols-4 gap-2">
                    {Object.entries(result.key_metrics).map(([k, v]) => (
                      <div key={k} className="bg-zinc-50 border border-zinc-200 rounded-md p-2.5">
                        <div className="text-[10px] text-zinc-500 font-mono uppercase">{k}</div>
                        <div className="font-mono text-lg font-semibold text-zinc-950">{String(v)}</div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <AiList icon={AlertOctagon} title={t("critical_tasks")} items={result.critical_tasks} renderField={(x) => ({ tag: x.task_id, title: x.name, sub: x.reason })} />
              <AiList icon={AlertTriangle} title={t("bottlenecks")} items={result.bottlenecks} renderField={(x) => ({ tag: x.task_id, title: x.name, sub: x.explanation, badge: x.impact })} />
              <AiList icon={TrendingDown} title={t("delays_at_risk")} items={result.delays_at_risk} renderField={(x) => ({ tag: x.task_id, title: x.name, sub: x.rationale, badge: x.risk })} />
              <AiList icon={Wrench} title={t("optimization_suggestions")} items={result.optimization_suggestions} renderField={(x) => ({ tag: x.area, title: x.suggestion, sub: x.expected_gain_days ? `+${x.expected_gain_days}d` : undefined })} />
              <AiList icon={AlertTriangle} title={t("inconsistencies")} items={result.inconsistencies} renderField={(x) => ({ tag: x.type, title: x.detail, sub: x.task_id })} />
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function AiList({ icon: Icon, title, items, renderField }) {
  if (!items || items.length === 0) return null;
  return (
    <section>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={12} className="text-cs-accent" />
        <div className="cs-label">{title}</div>
      </div>
      <ul className="space-y-2">
        {items.slice(0, 8).map((x, i) => {
          const f = renderField(x);
          const stableKey = `${f.tag || "item"}-${i}`;
          return (
            <li key={stableKey} className="border border-zinc-200 rounded-md p-2.5 hover:border-zinc-300">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase text-zinc-500">{f.tag}</div>
                  <div className="text-sm text-zinc-900 font-medium">{f.title}</div>
                  {f.sub && <div className="text-xs text-zinc-600 mt-1">{f.sub}</div>}
                </div>
                {f.badge && <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${f.badge === "high" ? "bg-red-50 text-red-700" : f.badge === "medium" ? "bg-amber-50 text-amber-700" : "bg-zinc-100 text-zinc-600"}`}>{f.badge}</span>}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
